/**
 * §10.2 Our Story — four narrative chapters (Origin → Problem →
 * Solution → Vision) plus a pull-quote.
 */

import React from 'react'
import {
  Rocket, AlertTriangle, Sparkles, Compass, Quote,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { STORY_CHAPTERS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Rocket, AlertTriangle, Sparkles, Compass }

export default function Story() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Our Story</Eyebrow>
          <SectionHeading align="center"
            title={<>The story behind{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Kalki Ai Cloud.</span></>}
            subtitle="A purpose-first narrative in four chapters — origin, problem, solution and vision — for the operators who want to build the next generation of trading businesses."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT — pull quote */}
          <div className="lg:col-span-4">
            <div className="relative rounded-3xl border border-brand-gold/30 bg-gradient-to-b from-brand-gold/[0.06] to-white/[0.01] p-6 backdrop-blur-xl overflow-hidden h-full flex flex-col justify-between gap-6">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
              <Quote className="relative h-8 w-8 text-brand-gold/70" />
              <div className="relative text-[17px] md:text-[19px] font-semibold text-white leading-snug">
                &ldquo;We didn&apos;t start Kalki Ai Cloud to sell software.  We started it to remove
                every reason an ambitious operator ever gives up on launching their own trading
                business.&rdquo;
              </div>
              <div className="relative flex items-center gap-2 text-[11.5px] font-mono text-brand-gold uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
                Founding team · Kalki Ai Cloud
              </div>
            </div>
          </div>

          {/* RIGHT — 4 chapter cards */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STORY_CHAPTERS.map((c, i) => {
              const tone = TONE[c.tone] || TONE.orange
              const Icon = ICONS[c.iconKey] || Sparkles
              return (
                <div key={c.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden flex flex-col gap-3">
                  <div className="pointer-events-none absolute inset-0 opacity-40"
                    style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                  <div className="relative flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                    </span>
                    <span className="text-[10.5px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>Chapter {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="relative">
                    <h4 className="text-[15px] font-semibold text-white leading-tight">{c.label}</h4>
                    <p className="text-[13px] text-zinc-300 leading-relaxed mt-2">{c.body}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Container>
    </section>
  )
}
